import { storeToRefs } from "pinia";
import { useBaseToast } from "~/composables/useBaseToast";
import { useApiHeaders } from "~/composables/useApiHeaders";
import { useExportCompanionDocumentFromOps } from "~/composables/useExportCompanionDocumentFromOps";
import { usePickGoogleDriveDestination } from "~/composables/usePickGoogleDriveDestination";
import { useSubmittedDocumentStore } from "~/composables/useSubmittedDocumentStore";
import { ExportDocumentFormat } from "~/types/enums/ExportDocumentFormat.enum";

const normalizeGoogleDocName = (fileName?: string) => {
  const baseName = fileName?.trim();
  return baseName && baseName.length > 0 ? baseName : "Document";
};

const toDocxUploadFileName = (fileName: string) => {
  return fileName.toLowerCase().endsWith(".docx") ? fileName : `${fileName}.docx`;
};

export const useExportCompanionDocumentToGoogleDrive = async (
  documentId: string,
  options?: {
    onProcessingStateChange?: (isProcessing: boolean) => void;
  }
) => {
  const documentStore = useSubmittedDocumentStore(documentId);
  const { submittedDocument } = storeToRefs(documentStore);

  await usePickGoogleDriveDestination().pickGoogleDriveDestination({
    onDestinationPicked: async ({ folder, accessToken }) => {
      options?.onProcessingStateChange?.(true);

      try {
        const fileName = normalizeGoogleDocName(submittedDocument.value?.name);
        const blob = await useExportCompanionDocumentFromOps(
          documentId,
          ExportDocumentFormat.docx,
          true,
          false,
          true,
          submittedDocument.value?.name,
          true,
          true
        );

        if (!blob) {
          return;
        }

        const headers = await useApiHeaders();
        delete headers["Content-Type"];

        const formData = new FormData();
        formData.append("file", blob, toDocxUploadFileName(fileName));
        formData.append("fileName", fileName);
        formData.append("folderId", folder.driveFileId);
        formData.append("accessToken", accessToken);

        const response = await fetch("/api/drive/files/google-doc", {
          method: "POST",
          headers,
          body: formData,
        });

        if (!response.ok) {
          throw new Error("Failed to create Google Doc");
        }

        const data = await response.json();
        const savedFileName = data?.name || fileName;

        useBaseToast(`Saved ${savedFileName} to Google Drive.`, "success");
      } finally {
        options?.onProcessingStateChange?.(false);
      }
    },
    onError: async () => {
      options?.onProcessingStateChange?.(false);
      useBaseToast(
        "Oops! Something went wrong exporting to Google Drive.",
        "error"
      );
    },
    onCancel: async () => {
      options?.onProcessingStateChange?.(false);
    },
  });
};
